import { useState } from "react";
import api, { formatApiErrorDetail, notifyDelete } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, TreePine } from "lucide-react";
import CampDialog from "@/components/CampDialog";
import CampCard from "@/components/CampCard";
import useCampSignups from "@/hooks/useCampSignups";

const today = () => new Date().toISOString().slice(0, 10);
const emptyCamp = { title: "", location: "", start_date: today(), end_date: "", fee: 0, notes: "" };

export default function Camps() {
  const [openCamp, setOpenCamp] = useState(false);
  const [form, setForm] = useState(emptyCamp);
  const { camps, youthMembers, expanded, signups, load, toggleExpand, toggleSignup, togglePaid, deleteCamp } = useCampSignups();

  const saveCamp = async () => {
    try {
      await api.post("/camps", { ...form, fee: Number(form.fee) || 0, end_date: form.end_date || null });
      toast.success("Camp created");
      setOpenCamp(false);
      setForm(emptyCamp);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const delCamp = async (id) => {
    if (!window.confirm("Delete this camp, its sign-ups and linked ledger entries?")) return;
    const data = await deleteCamp(id);
    notifyDelete(data, "Camp deleted");
  };

  return (
    <div className="space-y-6" data-testid="camps-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Events</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Camp Register</h1>
          <p className="text-muted-foreground text-sm mt-1">Track sign-ups and camp fees separately from weekly meetings.</p>
        </div>
        <Button data-testid="add-camp-btn" onClick={() => { setForm(emptyCamp); setOpenCamp(true); }} className="min-h-[44px]">
          <Plus className="w-4 h-4 mr-1.5" /> New camp
        </Button>
      </div>

      {camps.length === 0 && (
        <Card className="card-shadow-soft"><CardContent className="p-10 text-center text-muted-foreground" data-testid="camps-empty">
          <TreePine className="w-10 h-10 mx-auto mb-3 text-primary/40" />
          No camps yet — create your first event to track sign-ups and fees.
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {camps.map((c) => (
          <CampCard key={c.id} camp={c} expanded={expanded === c.id} members={youthMembers} signups={signups}
            onExpand={toggleExpand} onDelete={delCamp} onToggleSignup={toggleSignup} onTogglePaid={togglePaid} />
        ))}
      </div>

      <CampDialog open={openCamp} onOpenChange={setOpenCamp} form={form} setForm={setForm} onSave={saveCamp} />
    </div>
  );
}
